/**
 * FormPrint - 專業打印文件頁面
 * 
 * 第一性原則設計：
 * 1. 核心目的：產生正式、專業、完整的申請文件
 * 2. 打印優先：所有設計都以打印效果為首要考量
 * 3. 數據完整：確保所有關鍵信息都能正確呈現
 * 4. 避免斷頁：使用 break-inside-avoid 確保內容完整性
 */

import React, { useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { useFormDetail, useFormConfig } from '../../../hooks/useApi';
import { useI18n, getText } from '../../context/I18nContext';

// ========== 工具函數 ==========

const formatDate = (date, locale = 'zh-TW') => {
    if (!date) return '-';
    return new Date(date).toLocaleDateString(locale, {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
    });
};

const formatDateTime = (date, locale = 'zh-TW') => {
    if (!date) return '-';
    return new Date(date).toLocaleString(locale, {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
    });
};

const formatCurrency = (value, currency = 'CNY') => {
    if (value === undefined || value === null) return '-';
    const num = Number(value);
    if (isNaN(num)) return value;
    
    const symbols = {
        CNY: '¥',
        USD: '$',
        EUR: '€',
        JPY: '¥',
        NTD: 'NT$',
        HKD: 'HK$'
    };
    
    return `${symbols[currency] || ''}${num.toLocaleString()}`;
};

// ========== 子組件 ==========

/**
 * 文件頭部 - 公司標題和文件名稱
 */
const DocumentHeader = ({ formName, applicationId, status, t, getText }) => {
    const statusColors = {
        draft: { bg: 'bg-gray-100', text: 'text-gray-700', label: '草稿' },
        pending: { bg: 'bg-yellow-100', text: 'text-yellow-700', label: '審批中' },
        approved: { bg: 'bg-green-100', text: 'text-green-700', label: '已核准' },
        rejected: { bg: 'bg-red-100', text: 'text-red-700', label: '已駁回' },
        cancelled: { bg: 'bg-gray-100', text: 'text-gray-500', label: '已取消' },
        returned: { bg: 'bg-orange-100', text: 'text-orange-700', label: '已退回' },
    };

    const statusStyle = statusColors[status] || statusColors.draft;

    return (
        <div className="text-center border-b-2 border-gray-800 pb-4 mb-6">
            {/* 公司名稱 */}
            <div className="text-sm text-gray-500 mb-1">LUMINUS</div>
            
            {/* 文件標題 */}
            <h1 className="text-2xl font-bold text-gray-900 mb-3">
                {getText(formName) || t('common:applicationForm')}
            </h1>
            
            {/* 申請單號和狀態 */}
            <div className="flex justify-center items-center gap-4 text-sm">
                <span className="text-gray-600">
                    {t('common:applicationId')}: <span className="font-mono font-medium">{applicationId}</span>
                </span>
                <span className={`px-3 py-1 rounded-full text-xs font-medium ${statusStyle.bg} ${statusStyle.text}`}>
                    {t(`forms:status.${status}`) || statusStyle.label}
                </span>
            </div>
        </div>
    );
};

/**
 * 申請人信息區塊
 */
const ApplicantSection = ({ applicant, createdAt, submittedAt, t, language }) => {
    const rows = [
        { label: t('common:applicant'), value: applicant?.displayName },
        { label: t('profile:department'), value: applicant?.department },
        { label: t('profile:jobTitle'), value: applicant?.jobTitle },
        { label: t('profile:email'), value: applicant?.email },
        { label: t('profile:officeLocation'), value: applicant?.officeLocation },
        { label: t('forms:applicationDate'), value: formatDate(createdAt, language) },
        ...(submittedAt ? [{ label: t('forms:submissionDate'), value: formatDate(submittedAt, language) }] : []),
    ].filter(row => row.value);

    return (
        <section className="mb-6 break-inside-avoid">
            <h2 className="text-base font-bold text-gray-900 bg-gray-100 px-3 py-2 border-l-4 border-blue-600 mb-0">
                {t('common:applicantInfo')}
            </h2>
            <table className="w-full text-sm border border-gray-300 border-t-0">
                <tbody>
                    {rows.map((row, idx) => (
                        <tr key={idx} className="border-b border-gray-200 last:border-b-0">
                            <td className="py-2 px-3 font-medium text-gray-600 bg-gray-50 w-1/4 border-r border-gray-200">
                                {row.label}
                            </td>
                            <td className="py-2 px-3 text-gray-900">{row.value}</td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </section>
    );
};

/**
 * 表單內容區塊 - 按 Section 分組顯示
 */
const FormContentSection = ({ formConfig, formData, currency, t, getText, language }) => {
    if (!formConfig?.sections || !formData) return null;

    const { sections, fields } = formConfig;

    // 渲染欄位值
    const renderFieldValue = (field, value) => {
        if (value === undefined || value === null || value === '') {
            return <span className="text-gray-400">-</span>;
        }

        // 多語言物件
        if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
            if (value[language] || value['en'] || value['zh-TW']) {
                return getText(value);
            }
        }

        // 根據欄位類型處理
        switch (field?.type) {
            case 'currency':
                return formatCurrency(value, currency || field?.currency);
            
            case 'number':
                return field?.suffix ? `${value} ${field.suffix}` : value;
            
            case 'date':
                return formatDate(value, language);
            
            case 'datetime':
                return formatDateTime(value, language);
            
            case 'select':
            case 'radio':
                const option = field?.options?.find(opt => opt.value === value);
                return option ? getText(option.label) : value;
            
            case 'checkbox':
                if (Array.isArray(value)) {
                    const labels = value.map(v => {
                        const opt = field?.options?.find(o => o.value === v);
                        return opt ? getText(opt.label) : v;
                    });
                    return labels.join('、') || '-';
                }
                return value ? '是' : '否';
            
            case 'textarea':
                return (
                    <div className="whitespace-pre-wrap max-h-32 overflow-hidden">
                        {value}
                    </div>
                );
            
            default:
                return String(value);
        }
    };

    return (
        <>
            {sections.map((section) => {
                // 過濾出有值的欄位
                const sectionFields = section.fields
                    .map(fieldId => ({ fieldId, field: fields[fieldId], value: formData[fieldId] }))
                    .filter(({ field }) => field);

                if (sectionFields.length === 0) return null;

                return (
                    <section key={section.id} className="mb-6 break-inside-avoid">
                        <h2 className="text-base font-bold text-gray-900 bg-gray-100 px-3 py-2 border-l-4 border-blue-600 mb-0">
                            {getText(section.title)}
                        </h2>
                        <table className="w-full text-sm border border-gray-300 border-t-0">
                            <tbody>
                                {sectionFields.map(({ fieldId, field, value }) => (
                                    <tr key={fieldId} className="border-b border-gray-200 last:border-b-0">
                                        <td className="py-2 px-3 font-medium text-gray-600 bg-gray-50 w-1/4 border-r border-gray-200 align-top">
                                            {getText(field.label)}
                                            {field.required && <span className="text-red-500 ml-0.5">*</span>}
                                        </td>
                                        <td className="py-2 px-3 text-gray-900">
                                            {renderFieldValue(field, value)}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </section>
                );
            })}
        </>
    );
};

/**
 * 審批流程區塊 - 顯示完整審批鏈
 */
const ApprovalFlowSection = ({ approvalFlow, t, getText, language }) => {
    if (!approvalFlow || approvalFlow.length === 0) return null;

    // 過濾出審批步驟（不包含 completed）
    const approvalSteps = approvalFlow.filter(
        step => step.stepType === 'approval' || step.stepType === 'submit'
    );

    const getStatusDisplay = (status) => {
        const styles = {
            completed: { bg: 'bg-green-100', text: 'text-green-700', icon: '✓' },
            approved: { bg: 'bg-green-100', text: 'text-green-700', icon: '✓' },
            current: { bg: 'bg-blue-100', text: 'text-blue-700', icon: '●' },
            pending: { bg: 'bg-yellow-100', text: 'text-yellow-700', icon: '○' },
            rejected: { bg: 'bg-red-100', text: 'text-red-700', icon: '✗' },
            skipped: { bg: 'bg-gray-100', text: 'text-gray-500', icon: '—' },
        };
        return styles[status] || styles.pending;
    };

    const getApprovalModeText = (mode) => {
        const modes = {
            sequential: t('forms:route.modeSequential'),
            any_one: t('forms:route.modeParallelAny'),
            parallel_any: t('forms:route.modeParallelAny'),
            parallel_all: t('forms:route.modeParallelAll'),
        };
        return modes[mode] || mode;
    };

    return (
        <section className="mb-6 break-inside-avoid">
            <h2 className="text-base font-bold text-gray-900 bg-gray-100 px-3 py-2 border-l-4 border-blue-600 mb-0">
                {t('common:approvalFlowStatus')}
            </h2>
            <table className="w-full text-sm border border-gray-300 border-t-0">
                <thead className="bg-gray-50">
                    <tr>
                        <th className="py-2 px-2 text-center border-b border-r border-gray-300 w-4">
                            {t('common:step')}
                        </th>
                        <th className="py-2 px-3 text-left border-b border-r border-gray-300 w-20">
                            {t('common:stepName')}
                        </th>
                        <th className="py-2 px-3 text-left border-b border-r border-gray-300 w-1/3">
                            {t('common:approver')}
                        </th>
                        <th className="py-2 px-2 text-center border-b border-r border-gray-300 w-20">
                            {t('common:approvalMode')}
                        </th>
                        <th className="py-2 px-2 text-center border-b border-gray-300 w-24">
                            {t('common:statusLabel')}
                        </th>
                    </tr>
                </thead>
                <tbody>
                    {approvalSteps.map((step, idx) => {
                        const statusDisplay = getStatusDisplay(step.status);
                        const isCurrent = step.status === 'current';
                        
                        return (
                            <tr 
                                key={step.stepId} 
                                className={`border-b border-gray-200 last:border-b-0 ${isCurrent ? 'bg-blue-50' : ''}`}
                            >
                                <td className="py-2 px-2 text-center font-medium border-r border-gray-200">
                                    {idx + 1}
                                </td>
                                <td className="py-2 px-3 border-r border-gray-200">
                                    <div className="font-medium">
                                        {getText(step.stepName)}
                                    </div>
                                    {step.isOverridden && (
                                        <span className="text-xs text-amber-600">({t('forms:view.overridden')})</span>
                                    )}
                                </td>
                                <td className="py-2 px-3 border-r border-gray-200">
                                    {step.approvers?.length > 0 ? (
                                        <div className="space-y-0.5">
                                            {step.approvers.map((approver, i) => (
                                                <div key={i} className="text-sm">
                                                    <span>{approver.displayName}</span>
                                                    {approver.jobTitle && (
                                                        <span className="text-xs text-gray-500 ml-1">
                                                            ({approver.jobTitle})
                                                        </span>
                                                    )}
                                                </div>
                                            ))}
                                        </div>
                                    ) : (
                                        <span className="text-gray-400">-</span>
                                    )}
                                </td>
                                <td className="py-2 px-2 text-center text-xs border-r border-gray-200">
                                    {step.approvers?.length > 1 ? getApprovalModeText(step.approvalMode) : '-'}
                                </td>
                                <td className="py-2 px-2 text-center">
                                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium ${statusDisplay.bg} ${statusDisplay.text}`}>
                                        <span>{statusDisplay.icon}</span>
                                        <span>{t(`common:status.${step.status}`)}</span>
                                    </span>
                                </td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>
        </section>
    );
};

/**
 * 審批歷史區塊
 */
const ApprovalHistorySection = ({ approvalHistory, t, language }) => {
    if (!approvalHistory || approvalHistory.length === 0) return null;

    const getActionStyle = (action) => {
        const styles = {
            approve: { bg: 'bg-green-100', text: 'text-green-700' },
            approved: { bg: 'bg-green-100', text: 'text-green-700' },
            reject: { bg: 'bg-red-100', text: 'text-red-700' },
            rejected: { bg: 'bg-red-100', text: 'text-red-700' },
            return: { bg: 'bg-orange-100', text: 'text-orange-700' },
            returned: { bg: 'bg-orange-100', text: 'text-orange-700' },
            submit: { bg: 'bg-blue-100', text: 'text-blue-700' },
            submitted: { bg: 'bg-blue-100', text: 'text-blue-700' },
        };
        return styles[action] || { bg: 'bg-gray-100', text: 'text-gray-700' };
    };

    const getActionText = (action) => {
        // 使用 forms:action namespace
        const actionKey = action.replace('ed', ''); // approved -> approve
        return t(`forms:action.${actionKey}`) || action;
    };

    return (
        <section className="mb-6 break-inside-avoid">
            <h2 className="text-base font-bold text-gray-900 bg-gray-100 px-3 py-2 border-l-4 border-blue-600 mb-0">
                {t('common:approvalHistory')}
            </h2>
            <table className="w-full text-sm border border-gray-300 border-t-0">
                <thead className="bg-gray-50">
                    <tr>
                        <th className="py-2 px-3 text-left border-b border-r border-gray-300 w-28">
                            {t('common:time')}
                        </th>
                        <th className="py-2 px-3 text-left border-b border-r border-gray-300 w-1/3">
                            {t('common:approver')}
                        </th>
                        <th className="py-2 px-2 text-center border-b border-r border-gray-300 w-20">
                            {t('common:actions')}
                        </th>
                        <th className="py-2 px-3 text-left border-b border-gray-300">
                            {t('common:comment')}
                        </th>
                    </tr>
                </thead>
                <tbody>
                    {approvalHistory.map((history, idx) => {
                        const actionStyle = getActionStyle(history.action);
                        return (
                            <tr key={idx} className="border-b border-gray-200 last:border-b-0">
                                <td className="py-2 px-3 border-r border-gray-200 whitespace-nowrap text-xs">
                                    {formatDateTime(history.timestamp, language)}
                                </td>
                                <td className="py-2 px-3 border-r border-gray-200">
                                    <div>{history.actionBy?.displayName}</div>
                                    {history.actionBy?.jobTitle && (
                                        <div className="text-xs text-gray-500">
                                            {history.actionBy.jobTitle}
                                        </div>
                                    )}
                                </td>
                                <td className="py-2 px-2 text-center border-r border-gray-200">
                                    <span className={`px-2 py-0.5 rounded text-xs font-medium ${actionStyle.bg} ${actionStyle.text}`}>
                                        {getActionText(history.action)}
                                    </span>
                                </td>
                                <td className="py-2 px-3">
                                    {history.comment || '-'}
                                </td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>
        </section>
    );
};

/**
 * 簽核欄位區塊 - 用於實體簽章
 */
const SignatureBoxes = ({ approvalFlow, t, getText }) => {
    if (!approvalFlow || approvalFlow.length === 0) return null;

    // 只顯示審批類型的步驟
    const approvalSteps = approvalFlow.filter(step => step.stepType === 'approval');
    
    if (approvalSteps.length === 0) return null;

    return (
        <section className="mb-6 break-inside-avoid">
            <h2 className="text-base font-bold text-gray-900 bg-gray-100 px-3 py-2 border-l-4 border-blue-600 mb-3">
                {t('common:signatureArea')}
            </h2>
            <div className="grid grid-cols-3 gap-3">
                {approvalSteps.slice(0, 6).map((step) => (
                    <div 
                        key={step.stepId} 
                        className={`border p-3 text-center ${
                            step.isOverridden ? 'border-amber-400' : 'border-gray-300'
                        }`}
                    >
                        <div className="font-medium text-sm mb-1 text-gray-700">
                            {getText(step.stepName)}
                        </div>
                        <div className="text-xs text-gray-500 mb-2 h-4 overflow-hidden">
                            {step.approvers?.map(a => a.displayName).join(', ') || '-'}
                        </div>
                        <div className="h-16 border-b border-gray-300 mb-2">
                            {/* 簽名區域, 如果是已跳過, 則在簽名內顯示一個斜的浮水印, SKIPPED */}
                            {step.status === 'skipped' ? (
                                <div className="relative w-full h-full flex items-center justify-center">
                                    <div className="text-red-300 font-bold uppercase whitespace-nowrap select-none text-4xl tracking-widest" style={{ transform: 'rotate(-30deg)' }}>
                                        SKIPPED
                                    </div>
                                </div>
                            ) : null}
                        </div>
                        <div className="text-xs text-gray-500">
                            {t('common:date')}: __________
                        </div>
                    </div>
                ))}
            </div>
        </section>
    );
};

/**
 * 水印組件
 */
const Watermark = ({ status }) => {
    const watermarkTexts = {
        draft: 'DRAFT',
        rejected: 'REJECTED',
        cancelled: 'CANCELLED',
        returned: 'RETURNED',
    };

    const text = watermarkTexts[status];
    if (!text) return null;

    return (
        <div className="fixed inset-0 pointer-events-none z-50 overflow-hidden print:block" style={{ opacity: 0.06 }}>
            <div className="absolute inset-0 flex items-center justify-center" style={{ transform: 'rotate(-30deg)' }}>
                <div className="text-gray-500 font-bold uppercase whitespace-nowrap select-none text-8xl tracking-widest">
                    {text}
                </div>
            </div>
            {/* 重複水印覆蓋 */}
            <div className="absolute inset-0 grid grid-cols-2 grid-rows-3">
                {[...Array(6)].map((_, i) => (
                    <div key={i} className="flex items-center justify-center" style={{ transform: 'rotate(-30deg)' }}>
                        <span className="text-gray-500 font-bold uppercase whitespace-nowrap select-none text-5xl tracking-wider">
                            {text}
                        </span>
                    </div>
                ))}
            </div>
        </div>
    );
};

/**
 * 頁尾組件
 */
const DocumentFooter = ({ language, t }) => (
    <footer className="mt-8 pt-4 border-t border-gray-300 text-xs text-gray-500 flex justify-between">
        <span>
            {t('common:printDate')}: {formatDateTime(new Date(), language)}
        </span>
        <span>
            {t('common:systemGenerated')}
        </span>
    </footer>
);

/**
 * 打印工具列
 */
const PrintToolbar = ({ t }) => (
    <div className="print:hidden fixed top-4 right-4 z-50 flex gap-2">
        <button
            onClick={() => window.print()}
            className="px-5 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 shadow-lg flex items-center gap-2 font-medium"
        >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
            </svg>
            {t('common:print')}
        </button>
        <button
            onClick={() => window.history.back()}
            className="px-5 py-2.5 bg-gray-600 text-white rounded-lg hover:bg-gray-700 shadow-lg font-medium"
        >
            {t('common:back')}
        </button>
    </div>
);

// ========== 主組件 ==========

const FormPrint = () => {
    const { applicationId } = useParams();
    const { t, getText, language } = useI18n(['common', 'forms', 'profile']);

    // 數據查詢
    const { data: formResult, isLoading: formLoading, error: formError } = useFormDetail(applicationId);
    const form = formResult?.data;

    const { data: configResult, isLoading: configLoading } = useFormConfig(form?.formId);
    const formConfig = configResult?.data?.form;

    // 從表單數據中獲取貨幣
    const currency = useMemo(() => form?.formData?.currency || 'CNY', [form]);

    // Loading 狀態
    if (formLoading || configLoading) {
        return (
            <div className="flex justify-center items-center min-h-screen bg-gray-50">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
                    <p className="text-gray-600">{t('common:loading') || '載入中...'}</p>
                </div>
            </div>
        );
    }

    // 錯誤狀態
    if (formError || !form) {
        return (
            <div className="flex justify-center items-center min-h-screen bg-gray-50">
                <div className="bg-red-50 border border-red-200 rounded-lg p-6 max-w-md text-center">
                    <div className="text-red-600 text-lg font-medium mb-2">
                        {t('common:error') || '錯誤'}
                    </div>
                    <p className="text-red-700">
                        {formError?.message || t('common:formNotFound') || '找不到表單'}
                    </p>
                    <button
                        onClick={() => window.history.back()}
                        className="mt-4 px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
                    >
                        {t('common:back') || '返回'}
                    </button>
                </div>
            </div>
        );
    }

    return (
        <>
            {/* 水印 */}
            <Watermark status={form.status} />

            {/* 打印工具列 */}
            <PrintToolbar t={t} />

            {/* 打印內容 */}
            <div className="print-container max-w-4xl mx-auto p-8 bg-white min-h-screen">
                {/* 文件頭部 */}
                <DocumentHeader
                    formName={formConfig?.formName}
                    applicationId={form.applicationId}
                    status={form.status}
                    t={t}
                    getText={getText}
                />

                {/* 申請人信息 */}
                <ApplicantSection
                    applicant={form.applicant}
                    createdAt={form.createdAt}
                    submittedAt={form.submittedAt}
                    t={t}
                    language={language}
                />

                {/* 表單內容 */}
                <FormContentSection
                    formConfig={formConfig}
                    formData={form.formData}
                    currency={currency}
                    t={t}
                    getText={getText}
                    language={language}
                />

                {/* 審批流程 */}
                <ApprovalFlowSection
                    approvalFlow={form.approvalFlow}
                    t={t}
                    getText={getText}
                    language={language}
                />

                {/* 審批歷史 */}
                <ApprovalHistorySection
                    approvalHistory={form.approvalHistory}
                    t={t}
                    language={language}
                />

                {/* 簽核欄位 */}
                <SignatureBoxes
                    approvalFlow={form.approvalFlow}
                    t={t}
                    getText={getText}
                />

                {/* 頁尾 */}
                <DocumentFooter language={language} t={t} />
            </div>

            {/* 打印樣式 */}
            <style dangerouslySetInnerHTML={{ __html: `
                @media print {
                    @page {
                        size: A4 portrait;
                        margin: 12mm 10mm;
                    }
                    
                    body {
                        print-color-adjust: exact;
                        -webkit-print-color-adjust: exact;
                        font-size: 11pt;
                    }
                    
                    .print-container {
                        width: 100%;
                        max-width: none;
                        margin: 0;
                        padding: 0;
                        background: white;
                    }
                    
                    .print\\:hidden {
                        display: none !important;
                    }
                    
                    .break-inside-avoid {
                        break-inside: avoid;
                        page-break-inside: avoid;
                    }
                    
                    /* 確保表格邊框打印 */
                    table {
                        border-collapse: collapse;
                    }
                    
                    th, td {
                        border: 1px solid #d1d5db;
                    }
                    
                    /* 背景顏色打印 */
                    .bg-gray-50, .bg-gray-100 {
                        background-color: #f9fafb !important;
                    }
                    
                    .bg-green-100 { background-color: #dcfce7 !important; }
                    .bg-yellow-100 { background-color: #fef9c3 !important; }
                    .bg-red-100 { background-color: #fee2e2 !important; }
                    .bg-blue-100 { background-color: #dbeafe !important; }
                    .bg-blue-50 { background-color: #eff6ff !important; }
                    .bg-orange-100 { background-color: #ffedd5 !important; }
                    
                    /* 隱藏簽名框在沒有需要時 */
                    section:last-of-type {
                        break-after: avoid;
                    }
                }
                
                /* 螢幕預覽樣式 */
                @media screen {
                    body {
                        background-color: #f3f4f6;
                    }
                    
                    .print-container {
                        box-shadow: 0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1);
                        margin: 2rem auto;
                    }
                }
            ` }} />
        </>
    );
};

export default FormPrint;
